/**
 * AtmosphereModule — time-of-day distance fog / aerial perspective (Graphics G4).
 *
 * Sets `scene.fog` to an exponential distance fog whose COLOR tracks the time-of-day
 * band (warm at dawn/dusk, light-blue at noon, dark at night) and whose DENSITY tracks
 * time-of-day (hazier dawn/dusk/night, clear noon) AND the active weather (`WeatherProfile`
 * thickens it — rain/storm/fog). Scaled by the quality tier. The fog also masks far LOD/
 * impostor pop (G2). Mirrors LightingModule's SkyState read; runs before the post chain.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import type { DbConnection } from '../module_bindings/index.js';
import { atlas } from '../state.js';
import { farTierFog, fogWallDensity } from '../lib/terrain_far_tier.js';
import { worldEnv, TINT_BLEND } from '../lib/world_env.js';
import { bus } from '../bus.js';
import { type Band, bandForTimeOfDay, lerpColorHex, nightFactor, sunDirection } from '../lib/lighting.js';
import { activePreset, currentDrawDistance, distantDetailOn } from '../lib/quality_tiers.js';
import { resolveWeather, pickWeatherOverride } from '../lib/weather.js';
import { currentTod } from './SkyModule.js';

/** Per-band aerial-perspective fog tint + base density. */
const BAND_FOG: Record<Band, { color: string; density: number }> = {
  dawn: { color: '#c9a888', density: 0.0035 },
  day: { color: '#accfe6', density: 0.0012 },
  dusk: { color: '#c98858', density: 0.0035 },
  night: { color: '#161e36', density: 0.0030 },
};

/**
 * Underwater fog override (perf-critical contract): the scene's FogExp2 is
 * created ONCE and only ever MUTATED — swapping the `scene.fog` OBJECT forces
 * every material program to rebuild (a 1–2 s hitch on BOTH submerge and
 * surface). AtlasWaterModule sets/clears this instead of touching scene.fog.
 */
let underwaterOverride: { color: number; density: number } | null = null;
let refreshFog: (() => void) | null = null;

export function setUnderwaterFog(state: { color: number; density: number } | null): void {
  underwaterOverride = state;
  refreshFog?.();
}

export class AtmosphereModule implements IModule {
  readonly name = 'Atmosphere';
  private scene!: THREE.Scene;
  private meteoTimer: number | null = null;
  private readonly fog = new THREE.FogExp2(0x88aacc, 0.0015);
  private subscribed = false;

  private app: App | null = null;

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    this.scene.fog = this.fog;
    refreshFog = () => this.applyFromSky();
    bus.on('atlas:account-ready', () => { this.subscribe(); this.applyFromSky(); });
    for (const ev of ['atlas:environment-updated', 'atlas:world-entered'] as const) bus.on(ev, () => this.applyFromSky());
    // Fronts move (~11 m/s advection): re-apply on a slow tick so fog tracks
    // the meteo field even while the player stands still (the event-driven
    // path only fires on chunk/world changes).
    this.meteoTimer = window.setInterval(() => this.applyFromSky(), 5000);
  }

  private subscribe(): void {
    if (this.subscribed) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.subscribed = true;
    conn.subscriptionBuilder()
      .onApplied(() => this.applyFromSky())
      .subscribe(['SELECT * FROM sky_state', 'SELECT * FROM weather_profile', 'SELECT * FROM world']);
  }

  private commons(conn: DbConnection): { timeOfDay: number; weather: string } | null {
    for (const w of conn.db.world.iter()) {
      if (w.kind === 'commons') { const s = conn.db.skyState.worldId.find(w.worldId); if (s) return { timeOfDay: s.timeOfDay, weather: s.weather }; }
    }
    return null;
  }

  private weatherProfile(conn: DbConnection, weather: string) {
    return [...conn.db.weatherProfile.iter()].find((p) => p.weather === weather);
  }

  private applyFromSky(): void {
    const conn = atlas.conn;
    const c = conn ? this.commons(conn) : null;
    if (conn && c) {
      // currentTod() (not the raw commons clock): honors the dev/admin Local
      // time override and per-world clocks — the fog band always matches the
      // VISIBLE sky (previewing midnight via `\` gets midnight fog too).
      this.applyFog(currentTod(), c.weather, conn);
      return;
    }
    // No sky state yet — still honor the underwater override on the shared fog.
    if (underwaterOverride) {
      this.fog.color.setHex(underwaterOverride.color);
      this.fog.density = underwaterOverride.density;
    }
  }

  /** Compose the fog from the time-of-day band + the active weather profile + the tier. */
  applyFog(tod: number, weather: string, conn: DbConnection): void {
    const { band, next, blend } = bandForTimeOfDay(tod);
    const a = BAND_FOG[band], b = BAND_FOG[next];
    const todDensity = THREE.MathUtils.lerp(a.density, b.density, blend);
    const todColor = lerpColorHex(a.color, b.color, blend);
    // Fog density comes from the METEO-resolved weather at the player position
    // on the SHARED clock — radiation fog forms at humid calm dawns, precip
    // adds the far-field haze, and fronts sweep the fog with them. Spatially
    // correct wetland/highland fog without WeatherModule state or a circular
    // import (resolveWeather is pure).
    const seed = this.commonsSeed(conn);
    // The SAME override precedence the WeatherModule resolves with (its Local
    // debug pin over the live-ops kind) — a pinned sandstorm tints the fog and a
    // pinned rain greys it; before, the fog resolved the natural field alone.
    const wm = this.app?.getModule('Weather') as { debugWeather?: string | null } | undefined;
    const override = pickWeatherOverride(wm?.debugWeather ?? null, weather && weather !== 'clear' ? weather : undefined);
    const resolved = resolveWeather(seed, atlas.localX, atlas.localZ, Date.now() / 1000, override, tod);
    const biomeDensity = resolved.fogDensity;
    const wp = this.weatherProfile(conn, weather);
    const kindDensity = wp?.fogDensity ?? 0;
    // WHY (the night "glowing fog" bug): weather_profile.fogColorHex is authored
    // in DAYLIGHT (clear #bcd4ec, fog #c8cdd2, …). It was blended in unscaled at
    // a weight keyed to the BIOME mist density — so even a clear night composed a
    // bright grey-blue fog that lit distant objects up. Two changes:
    //   1. the grey-out weight keys off the weather KIND's density (grey skies
    //      grey the fog; biome mist only thickens it),
    //   2. the weather tint is illumination-scaled by the sun before blending,
    //      so an overcast night reads dark grey — never noon haze. THREE.Color
    //      stores LINEAR components (r182 color management), so the scale is
    //      radiance-linear; the 0.01 floor ≈ a 0.12 apparent (sRGB) dim — the
    //      authored night-band/day-band ratio.
    const illum = 0.01 + 0.99 * (1 - nightFactor(sunDirection(tod).y));
    const weatherColor = wp ? new THREE.Color(wp.fogColorHex).multiplyScalar(illum) : todColor.clone();
    const weight = Math.min(0.8, kindDensity / 0.01);
    this.fog.color.copy(todColor).lerp(weatherColor, weight);
    // A SANDSTORM tints the fog orange-brown (the dust wall IS the visibility),
    // illumination-scaled like the weather tint so a night storm reads dark.
    if (resolved.fogTintHex && resolved.dust > 0.02) this.fog.color.lerp(new THREE.Color(resolved.fogTintHex).multiplyScalar(illum), Math.min(0.9, resolved.dust * 0.95));
    // Density superposes the time-of-day haze with the STRONGER of the spatial
    // biome mist and the weather kind's fog — tod still tracks (dawn/dusk/night
    // hazier than noon), wetlands stay mistier than deserts, and rain/storm/fog
    // kinds thicken it again (they had silently stopped contributing).
    this.fog.density = (todDensity + Math.max(biomeDensity, kindDensity)) * activePreset().fogScale;
    // Aerial perspective (the terrain far tier, 2026-09-03): a uniform density
    // tuned to hide the ground draw edge blankets the ground seen from 760 m
    // up — thin it with the viewer's height above ground (exponential height
    // fog, approximated at the camera; TerrainFarTierModule writes the factor).
    this.fog.density *= farTierFog.factor;
    // The world's authored ENVIRONMENT (Edit Mode): density multiplier + an
    // optional tint, on top of everything above — so a haunted swamp world can
    // be thick and green at noon while the Commons keeps its meteo fog.
    const env = worldEnv();
    this.fog.density *= env.fogMul;
    if (env.fogColorHex) this.fog.color.lerp(new THREE.Color(env.fogColorHex), TINT_BLEND);
    // Distant detail OFF (2026-09-08): a floor on the density so the fog
    // CLOSES at the draw distance — the void past it reads as haze.
    if (!distantDetailOn()) this.fog.density = Math.max(this.fog.density, fogWallDensity(currentDrawDistance()));
    // Underwater wins outright — same fog OBJECT, different values.
    if (underwaterOverride) {
      this.fog.color.setHex(underwaterOverride.color);
      this.fog.density = underwaterOverride.density;
    }
  }

  /** Returns the terrainSeed for the 'commons' world — mirroring WeatherModule.commonsSeed. */
  private commonsSeed(conn: DbConnection): string {
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') return w.terrainSeed;
    return 'commons';
  }

  /** Test/inspection hooks. */
  get fogColorHex(): string { return '#' + this.fog.color.getHexString(); }
  /** The module's own fog colour object — LIVE even when `scene.fog` is detached (probe rigs, space, personal presets); the flat sky copies it. */
  get fogColor(): THREE.Color { return this.fog.color; }
  get fogDensity(): number { return this.fog.density; }
  applyForTest(tod: number, weather: string): void { const conn = atlas.conn; if (conn) this.applyFog(tod, weather, conn); }

  dispose(): void {
    if (this.meteoTimer !== null) { window.clearInterval(this.meteoTimer); this.meteoTimer = null; }
  }
}
